import React from 'react';
import { useTranslation } from 'react-i18next';
import { GraphColorMode, NodeSizeMetric, SIZE_METRIC_LABELS, TOP_VOLUME_EDGE_COUNT } from './graphTypes';
import {
  COMMUNITY_PALETTE,
  CONNECTION_INBOUND_COLOR,
  CONNECTION_INTERNAL_COLOR,
  CONNECTION_OUTBOUND_COLOR,
  DIMMED_NODE_COLOR,
  PLATFORM_PALETTE,
  SCOPE_IN_NODE_COLOR,
  SCOPE_INBOUND_COLOR,
  SCOPE_OUT_NODE_COLOR,
  SCOPE_OUTBOUND_COLOR,
  ZSCORE_ELEVATED_COLOR,
  ZSCORE_HIGH_COLOR,
  ZSCORE_LOW_COLOR,
  ZSCORE_NORMAL_COLOR,
} from './graphColors';
import { MENTION_TYPE_COLORS } from './timelineUtils';
import { platformNamesMapping } from '../../utils/platforms';
import { LayoutMode } from './layoutPipeline';
import styles from './SocialGraphPOC.module.scss';

// One legend row (Figma node 353-153/165): a fixed-width swatch column — dot,
// or empty for text-only rows so all text stays aligned — followed by the
// description.
const Row: React.FC<{ dot?: string; children: React.ReactNode }> = ({ dot, children }) => (
  <span className={styles.overviewLegendItem}>
    <span className={styles.legendSwatchCol} aria-hidden="true">
      {dot && <span className={styles.legendSwatch} style={{ backgroundColor: dot }} />}
    </span>
    <span className={styles.legendRowText}>{children}</span>
  </span>
);

export interface GraphLegendProps {
  colorMode: GraphColorMode;
  zscoreEdgeColoring: boolean;
  platforms: { name: string; count: number }[];
  // platform → hue for this graph, by actor-count rank (rankPlatformColors)
  platformColors: Map<string, string>;
  // community ids present in the graph, largest first — listed only in
  // community mode (designer spec: no community list in other color schemes)
  communities: number[];
  effectiveSizeMetric: NodeSizeMetric;
  layoutMode: LayoutMode;
  // id of the community spotlighted on the canvas, null when none
  highlightedCommunity: number | null;
  // category spotlighted on the canvas: its members are repainted in this hue,
  // so the legend names the category with the same swatch
  highlightedCategory: { label: string; color: string } | null;
  // an actor is selected on the canvas — its edges are recolored cyan (inbound)
  // / orange (outbound) by the hover reducer, so the legend explains the pair
  actorSelected: boolean;
  // "Thickness by interactions" switch state — when on, thickness encodes
  // volume graph-wide and the reciprocal thickness bump doesn't apply, so the
  // captions below shift accordingly
  volumeSizedEdges: boolean;
  // "Highlight instead of hide" is on and at least one actor fails the facet
  // filters — those actors are painted the spotlight gray, so the legend says so
  filtersDimmed: boolean;
  // volume mode on a graph large enough to flag its heaviest pairs (see
  // TOP_VOLUME_MIN_EDGES) — those edges are rendered brightest with arrows
  topVolumeEdges: boolean;
}

// Whether the legend has any row to show for this state — the page hides the
// whole legend pill when it doesn't (PM rule 2026-10-16), e.g. Monochrome with
// nothing selected; turning on "Thickness by interactions" brings it back with
// the thickness caption. Must mirror the row conditions in GraphLegend below.
export const hasLegendContent = ({
  colorMode,
  zscoreEdgeColoring,
  platforms,
  communities,
  layoutMode,
  highlightedCommunity,
  highlightedCategory,
  actorSelected,
  volumeSizedEdges,
  filtersDimmed,
  topVolumeEdges,
}: GraphLegendProps): boolean =>
  layoutMode === 'radial' ||
  volumeSizedEdges ||
  topVolumeEdges ||
  zscoreEdgeColoring ||
  (colorMode === 'mention' && !zscoreEdgeColoring) ||
  (colorMode === 'connection' && !zscoreEdgeColoring && !actorSelected) ||
  (colorMode === 'platform' && platforms.length > 0) ||
  colorMode === 'scope' ||
  actorSelected ||
  filtersDimmed ||
  highlightedCategory !== null ||
  highlightedCommunity !== null ||
  (colorMode === 'community' && communities.length > 0);

// Legend body (Figma 826-16913 community mode / 826-16982 scrolled); the
// collapsible shell around it lives in SocialGraphPOC.tsx. Layout follows the
// design: text captions first, mode-specific dot rows after, and — only in
// "By community" — the community list at the bottom. The key-connector,
// node-size, node-color, edge-color and reciprocal-edge captions were dropped
// on PM request (2026-09-16).
export const GraphLegend: React.FC<GraphLegendProps> = ({
  colorMode,
  zscoreEdgeColoring,
  platforms,
  platformColors,
  communities,
  effectiveSizeMetric,
  layoutMode,
  highlightedCommunity,
  highlightedCategory,
  actorSelected,
  volumeSizedEdges,
  filtersDimmed,
  topVolumeEdges,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {layoutMode === 'radial' && (
        <Row>
          {t('Cluster center = highest {{metric}}; nodes sit farther out the lower their {{metric}}', {
            metric: t(SIZE_METRIC_LABELS[effectiveSizeMetric]),
          })}
        </Row>
      )}
      {volumeSizedEdges && <Row>{t('Thicker connection = more interactions')}</Row>}
      {topVolumeEdges && (
        <Row>{t('Brightest edges with arrows = top {{n}} relations by interactions', { n: TOP_VOLUME_EDGE_COUNT })}</Row>
      )}
      {zscoreEdgeColoring && (
        <>
          <Row dot={ZSCORE_HIGH_COLOR}>
            {t('Z > 2.0 — anomalously intense activity, suspected coordination or automated amplification')}
          </Row>
          <Row dot={ZSCORE_ELEVATED_COLOR}>
            {t('1.0 < Z ≤ 2.0 — interaction density well above the norm, needs analyst review')}
          </Row>
          <Row dot={ZSCORE_NORMAL_COLOR}>
            {t('0 ≤ Z ≤ 1.0 — natural, typical activity within the statistical norm')}
          </Row>
          <Row dot={ZSCORE_LOW_COLOR}>
            {t('Z < 0 — reduced activity, sporadic or fading interactions below the network average')}
          </Row>
        </>
      )}
      {colorMode === 'mention' && !zscoreEdgeColoring && (
        <>
          <Row dot={MENTION_TYPE_COLORS.MENTION}>{t('Mention')}</Row>
          <Row dot={MENTION_TYPE_COLORS.REPOST}>{t('Repost')}</Row>
          <Row dot={MENTION_TYPE_COLORS.LINK}>{t('Link')}</Row>
        </>
      )}
      {/* the actor spotlight repaints edges cyan / orange relative to the selected
          actor — its rows below take over, so don't show both edge pairs */}
      {colorMode === 'connection' && !zscoreEdgeColoring && !actorSelected && (
        <>
          {/* copy from the PM (2026-10-06): "Internal" = flow INTO the group,
              "External" = flow out of it; out ↔ out chatter stays background
              gray and has no row */}
          <Row dot={CONNECTION_INTERNAL_COLOR}>{t('Inside actor group — an actor in the actor group interacts with another actor in the same actor group')}</Row>
          <Row dot={CONNECTION_INBOUND_COLOR}>{t('Internal connection — an external actor interacts with an actor in the actor group')}</Row>
          <Row dot={CONNECTION_OUTBOUND_COLOR}>{t('External connection — an actor in the actor group interacts with an external actor')}</Row>
        </>
      )}
      {colorMode === 'platform' &&
        platforms.map(({ name, count }) => (
          <Row key={name} dot={platformColors.get(name) ?? PLATFORM_PALETTE[0]}>
            {`${platformNamesMapping[name] ?? name} (${count})`}
          </Row>
        ))}
      {/* the in/out-of-group node pair belongs to the scope scheme only — in every
          other color scheme scope isn't encoded on the canvas (designer spec) */}
      {colorMode === 'scope' && (
        <>
          <Row dot={SCOPE_IN_NODE_COLOR}>{t('In actor group')}</Row>
          <Row dot={SCOPE_OUT_NODE_COLOR}>{t('Not in actor group')}</Row>
        </>
      )}
      {actorSelected && (
        <>
          {/* PM copy 2026-10-16 */}
          <Row dot={SCOPE_INBOUND_COLOR}>{t('Inbound connection — other actors interact with the selected actor')}</Row>
          <Row dot={SCOPE_OUTBOUND_COLOR}>{t('Outbound connection — the selected actor interacts with other actors')}</Row>
          {/* with volume sizing on, the graph-wide thickness row above already
              covers it — this row explains the selection-only thickening */}
          {!volumeSizedEdges && <Row>{t('Thicker edge = more interactions with the selected actor')}</Row>}
        </>
      )}
      {filtersDimmed && <Row dot={DIMMED_NODE_COLOR}>{t('Actors outside the active filters are dimmed')}</Row>}
      {highlightedCategory && <Row dot={highlightedCategory.color}>{highlightedCategory.label}</Row>}
      {highlightedCommunity !== null && (
        <Row dot={DIMMED_NODE_COLOR}>
          {t('Community #{{id}} is spotlighted — actors outside it are dimmed', {
            id: highlightedCommunity,
          })}
        </Row>
      )}
      {colorMode === 'community' &&
        communities.map((id) => (
          <Row key={id} dot={COMMUNITY_PALETTE[id % COMMUNITY_PALETTE.length]}>
            {t('Community #{{id}}', { id })}
          </Row>
        ))}
    </>
  );
};
